import React, { useMemo } from 'react';
import * as THREE from 'three';

interface RockProps {
  position: [number, number, number];
  scale?: number;
  type?: 'normal' | 'boulder' | 'pointy' | 'flat';
}

export const Rock: React.FC<RockProps> = ({ position, scale = 1, type }) => {
  // Randomly select a rock type if not specified
  const rockType = useMemo(() => {
    if (type) return type;
    const types = ['normal', 'boulder', 'pointy', 'flat'];
    return types[Math.floor(Math.random() * types.length)] as
      | 'normal'
      | 'boulder'
      | 'pointy'
      | 'flat';
  }, [type]);

  // Randomize the rock appearance
  const { geometry, materialProps } = useMemo(() => {
    // Base random values
    const roughness = 0.7 + Math.random() * 0.3;
    const baseColor = new THREE.Color(
      Math.random() > 0.7
        ? '#A1887F' // Brown rock
        : Math.random() > 0.5
        ? '#9E9E9E' // Gray rock
        : '#78909C' // Blueish gray rock
    );

    // Apply slight color variation
    baseColor.multiplyScalar(0.8 + Math.random() * 0.4);

    // Different geometries based on rock type
    let geo: THREE.BufferGeometry;

    switch (rockType) {
      case 'boulder':
        // Large rounded rock
        geo = new THREE.SphereGeometry(1, 6, 6);
        // Distort the sphere
        const posAttr = geo.getAttribute('position');
        const vertices = posAttr.array;
        for (let i = 0; i < vertices.length; i += 3) {
          vertices[i] *= 1 + Math.random() * 0.2;
          vertices[i + 1] *= 1 + Math.random() * 0.2;
          vertices[i + 2] *= 1 + Math.random() * 0.2;
        }
        geo.computeVertexNormals();
        break;

      case 'pointy':
        // Pointy rock formation
        geo = new THREE.ConeGeometry(0.8, 2, 5);
        // Flatten it a bit
        geo.scale(1, 0.6, 1);
        // Rotate it a bit for randomness
        geo.rotateX(Math.random() * 0.5);
        geo.rotateY(Math.random() * Math.PI * 2);
        break;

      case 'flat':
        // Flat rock like a slate
        geo = new THREE.BoxGeometry(1.5, 0.4, 1.2);
        // Add some random rotation
        geo.rotateX(Math.random() * 0.2);
        geo.rotateY(Math.random() * Math.PI * 2);
        geo.rotateZ(Math.random() * 0.2);
        break;

      default: // 'normal'
        geo = new THREE.DodecahedronGeometry(1, 0);
        // Add some random distortion
        const positions = geo.getAttribute('position');
        const vertArray = positions.array;
        for (let i = 0; i < vertArray.length; i += 3) {
          vertArray[i] *= 0.8 + Math.random() * 0.4;
          vertArray[i + 1] *= 0.8 + Math.random() * 0.4;
          vertArray[i + 2] *= 0.8 + Math.random() * 0.4;
        }
        geo.computeVertexNormals();
        break;
    }

    return {
      geometry: geo,
      materialProps: {
        color: baseColor.getHex(),
        roughness,
        flatShading: true,
      },
    };
  }, [rockType]);

  // Create unique rotation and scaling
  const { uniqueScale, rotation } = useMemo(() => {
    // Get a random set of XYZ scales for some non-uniform scaling
    const xScale = scale * (0.8 + Math.random() * 0.4);
    const yScale = scale * (0.8 + Math.random() * 0.4);
    const zScale = scale * (0.8 + Math.random() * 0.4);

    return {
      uniqueScale: [xScale, yScale, zScale],
      rotation: [
        Math.random() * 0.5,
        Math.random() * Math.PI * 2,
        Math.random() * 0.5,
      ],
    };
  }, [scale]);

  return (
    <mesh
      position={position}
      scale={uniqueScale as any}
      rotation={rotation as any}
      castShadow
      receiveShadow
      geometry={geometry}
    >
      <meshStandardMaterial {...materialProps} />
    </mesh>
  );
};
